"use client";

import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  TablePagination,
  useTheme,
} from "@mui/material";
import { CalendarMonth as BookingsIcon } from "@mui/icons-material";
import { tokens } from "../../../../theme";

interface BookingResponse {
  id: number;
  title?: string;
  service?: string;
  package_item?: { name: string };
  services?: Array<{ date_of_service: string }>;
  deceased?: Array<{
    first_name?: string;
    surname?: string;
    person_image?: string;
  }>;
}

interface BookingsCardProps {
  bookings: BookingResponse[];
  totalBookings: number;
  bookingsLoading: boolean;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBookingClick: (bookingId: number) => void;
  formatDate: (date: string | undefined) => string;
  InfoCard: React.ComponentType<{
    title: string;
    icon: React.ReactNode;
    sx?: object;
    children: React.ReactNode;
  }>;
}

export const BookingsCard: React.FC<BookingsCardProps> = ({
  bookings,
  totalBookings,
  bookingsLoading,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onBookingClick,
  formatDate,
  InfoCard,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <InfoCard
      title="Previous Bookings"
      sx={{ height: "100%" }}
      icon={<BookingsIcon />}
    >
      {bookingsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : bookings.length === 0 ? (
        <Typography
          variant="h4"
          sx={{
            fontStyle: "italic",
            textAlign: "center",
            py: 4,
            fontFamily: '"Freehand", cursive',
          }}
        >
          No Related Bookings
        </Typography>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.primary[800] }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Package</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {bookings.map((booking, index) => (
                  <TableRow
                    key={booking.id}
                    onClick={() => onBookingClick(booking.id)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        index % 2 === 0 ? "white" : colors.primary[800],
                      "&:hover": {
                        backgroundColor: colors.grey[500],
                      },
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Avatar
                          src={booking?.deceased?.[0]?.person_image || ""}
                          sx={{ width: 32, height: 32 }}
                        >
                          {(
                            booking?.deceased?.[0]?.first_name?.[0] || ""
                          ).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2">
                          {booking?.deceased?.[0]?.first_name &&
                          booking?.deceased?.[0]?.surname
                            ? `${booking.deceased[0].first_name} ${booking.deceased[0].surname}`
                            : (booking?.title ?? "")}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{booking.service}</TableCell>
                    <TableCell>{booking.package_item?.name}</TableCell>
                    <TableCell>
                      {formatDate(booking.services?.[0]?.date_of_service)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalBookings}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </>
      )}
    </InfoCard>
  );
};

export default BookingsCard;
